import React from "react";
import { Link } from "react-router-dom";
import { Reveal, Eyebrow } from "./Decorative";
import {
  MEDIA_COVERAGE,
  MEDIA_PUBLICATIONS,
  publicationOf,
  longDate,
  domainOf,
} from "../data/mediaCoverage";

/* ============================================================================
   MASTHEAD — one publication's logo, on a white tile.
   Falls back to the name set in type when we do not hold the file, so the row
   is complete and correct from the first day and a logo only ever replaces a
   name. There is never a broken image or an empty gap.
   ========================================================================== */
function Masthead({ name, size = "lg" }) {
  const pub = publicationOf(name);
  const box = size === "lg" ? "h-16 w-[150px] sm:w-[168px]" : "h-12 w-[124px]";
  const cap = size === "lg" ? "max-h-10" : "max-h-7";
  if (!pub.logo) {
    // No masthead file yet: the paper's name. On the dark coverage page (sm)
    // it sits in a white tile like the logos do, so it reads the same way.
    return size === "lg" ? (
      <span className={`flex items-center justify-center ${box} px-3 font-display text-lg text-ink-2/80 whitespace-nowrap`}>
        {name}
      </span>
    ) : (
      <span className={`flex items-center justify-center ${box} px-3 bg-white rounded-[3px] border border-[#C9A961]/20 font-display text-base text-ink whitespace-nowrap`}>
        {name}
      </span>
    );
  }
  const img = (
    <img
      src={pub.logo}
      alt={name}
      width={pub.logoW}
      height={pub.logoH}
      loading="lazy"
      decoding="async"
      className={`${cap} max-w-full w-auto object-contain`}
    />
  );
  const cls = `flex items-center justify-center ${box} px-4 bg-white rounded-[3px] border border-[#C9A961]/20`;
  return pub.site ? (
    <a
      href={pub.site}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={`${name}, opens in a new tab`}
      className={`${cls} opacity-90 hover:opacity-100 transition-opacity`}
    >
      {img}
    </a>
  ) : (
    <span className={cls}>{img}</span>
  );
}

/* ============================================================================
   MEDIA STRIP — the home page mention. Who wrote about us, as their mastheads.
   Deliberately quiet: no counts, no "as featured in", one link through.
   ========================================================================== */
export function MediaStrip() {
  if (!MEDIA_PUBLICATIONS.length) return null;
  return (
    <section
      className="py-14 md:py-20 px-5 border-y border-gold/15"
      style={{ background: "linear-gradient(135deg, #FBF6EE 0%, #F2EAD9 100%)" }}
      data-testid="media-strip"
    >
      <div className="max-w-5xl mx-auto text-center">
        <Reveal>
          <Eyebrow>Media &amp; Coverage</Eyebrow>
          <ul className="mt-8 flex flex-wrap items-center justify-center gap-4 sm:gap-5">
            {MEDIA_PUBLICATIONS.map((name) => (
              <li key={name}>
                <Masthead name={name} />
              </li>
            ))}
          </ul>
          <Link
            to="/media-coverage/"
            className="inline-block mt-8 font-body text-sm tracking-wide uppercase text-[#8A6B22] hover:text-[#C9A961] transition-colors"
            data-testid="media-strip-cta"
          >
            Read the coverage →
          </Link>
        </Reveal>
      </div>
    </section>
  );
}

/* ============================================================================
   ONE PIECE. The headline is the whole of it: it links out where the article is
   online, and sits above the cutting where the piece ran only in print. Nothing
   is summarised or rewritten, so the paper's own words are what a reader gets.
   ========================================================================== */
// A press release carried word for word by several sites: one card, the
// release's own headline and Anshu's line from it, and every site as a link.
function ReleasePiece({ piece }) {
  return (
    <article className="border-l pl-6 py-2" style={{ borderColor: "rgba(201,169,97,0.25)" }} data-testid="media-release">
      <div className="flex items-center gap-4 flex-wrap">
        <span className="font-body text-xs tracking-[0.18em] uppercase rounded-full border px-3 py-1" style={{ color: "#E8D9C5", borderColor: "rgba(201,169,97,0.4)" }}>
          Press release
        </span>
        {piece.date ? (
          <span className="font-body text-xs tracking-[0.18em] uppercase" style={{ color: "#C9A961" }}>
            {longDate(piece.date)}
          </span>
        ) : null}
      </div>
      <p className="mt-4 font-display text-2xl sm:text-3xl leading-snug" style={{ color: "#FBF6EE" }}>{piece.headline}</p>
      {piece.excerpt ? (
        <p className="mt-5 font-body leading-relaxed max-w-[60ch] italic" style={{ color: "#E8D9C5" }}>
          {piece.excerpt}
          <span className="not-italic" style={{ color: "rgba(232,217,197,0.7)" }}> — Anshu Goyal</span>
        </p>
      ) : null}
      <p className="mt-6 font-body text-xs tracking-[0.18em] uppercase" style={{ color: "rgba(232,217,197,0.7)" }}>Carried by</p>
      <ul className="mt-3 flex flex-wrap gap-x-5 gap-y-2">
        {piece.carriedBy.map((c) => (
          <li key={c.url}>
            <a
              href={c.url}
              target="_blank"
              rel="noopener noreferrer"
              className="font-body text-sm underline underline-offset-4 hover:opacity-80"
              style={{ color: "#E5C880" }}
              aria-label={`Read on ${c.publication} (opens in new tab)`}
            >
              {c.publication}
            </a>
          </li>
        ))}
      </ul>
    </article>
  );
}

function Piece({ piece }) {
  if (piece.kind === "release") return <ReleasePiece piece={piece} />;
  const deva = piece.language === "hi" ? "font-deva" : "";
  const headline = (
    <span className={`font-display text-2xl sm:text-3xl leading-snug ${deva}`} style={{ color: "#FBF6EE" }}>
      {piece.headline}
    </span>
  );
  const pub = publicationOf(piece.publication);
  return (
    <article className="border-l pl-6 py-2" style={{ borderColor: "rgba(201,169,97,0.25)" }}>
      <div className="flex items-center gap-4 flex-wrap">
        <Masthead name={piece.publication} size="sm" />
        {piece.date ? (
          <span className="font-body text-xs tracking-[0.18em] uppercase" style={{ color: "#C9A961" }}>
            {longDate(piece.date)}
          </span>
        ) : null}
      </div>

      <div className="mt-4">
        {piece.url ? (
          <a
            href={piece.url}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`Read on ${piece.publication} (opens in new tab)`}
            className="group block"
          >
            {headline}
            <span className="block mt-4 font-body text-xs tracking-[0.18em] uppercase text-[#E8D9C5] group-hover:text-[#E5C880] transition-colors">
              Read the article →
            </span>
          </a>
        ) : (
          <>
            {headline}
            {/* A print piece is never given an "article" link it does not have.
                The e-paper is named as one, since it opens a page viewer rather
                than a story; where there is none, the line says where the piece
                ran and links to the paper itself. */}
            {piece.epaper ? (
              <a
                href={piece.epaper}
                target="_blank"
                rel="noopener noreferrer"
                className="block mt-4 font-body text-xs tracking-[0.18em] uppercase text-[#E8D9C5] hover:text-[#E5C880] transition-colors"
              >
                Read it in the e-paper →
              </a>
            ) : (
              <span className="block mt-4 font-body text-xs tracking-[0.18em] uppercase" style={{ color: "rgba(232,217,197,0.7)" }}>
                In print{piece.printedPage ? `, page ${piece.printedPage}` : null}
                {pub.site ? (
                  <>
                    {" · "}
                    <a
                      href={pub.site}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="hover:text-[#E5C880] transition-colors"
                    >
                      {domainOf(pub.site)} →
                    </a>
                  </>
                ) : null}
              </span>
            )}
          </>
        )}
      </div>

      {piece.excerpt ? (
        <p
          className={`mt-5 font-body leading-relaxed max-w-[52ch] ${piece.language === "hi" ? "font-deva" : "italic"}`}
          style={{ color: "rgba(232,217,197,0.85)" }}
        >
          {piece.excerpt}
        </p>
      ) : null}

      {piece.clipping ? (
        <figure className={`mt-6 ${piece.wide ? "max-w-none" : "max-w-[420px]"}`}>
          {/* The cutting opens at full size in its own tab. A broadsheet page set
              into a column reads as a headline and a photograph but not as an
              article, and a reader who wants the words should not have to
              squint at them. */}
          <a
            href={piece.clipping}
            target="_blank"
            rel="noopener noreferrer"
            className="group block"
            aria-label={`Open the ${piece.publication} cutting at full size`}
          >
            <div className="bg-white border transition-colors" style={{ borderColor: "rgba(201,169,97,0.25)" }}>
              <img
                src={piece.clipping}
                alt={piece.date ? `${piece.publication}, ${longDate(piece.date)}` : `${piece.publication}, cutting`}
                width={piece.clipW}
                height={piece.clipH}
                loading="lazy"
                decoding="async"
                className="w-full h-auto"
              />
            </div>
            <figcaption className="mt-3 font-body text-xs tracking-[0.18em] uppercase text-[#E8D9C5]/70 group-hover:text-[#E5C880] transition-colors">
              {piece.publication}, in print · Open at full size →
            </figcaption>
          </a>
        </figure>
      ) : null}
    </article>
  );
}

/* ============================================================================
   THE COVERAGE ITSELF, grouped by the evening it covered.
   ========================================================================== */
export function MediaPieces() {
  return (
    <>
      {MEDIA_COVERAGE.map((occasion) => (
        <section
          key={occasion.id}
          id={occasion.id}
          className="px-5 py-16 md:py-20"
          style={{ background: "linear-gradient(180deg,#1C1A17 0%,#0F0A07 100%)" }}
          data-testid={`media-occasion-${occasion.id}`}
        >
          <div className="max-w-6xl mx-auto">
            <Reveal>
              <div className="border-t pt-8" style={{ borderColor: "rgba(201,169,97,0.25)" }}>
                <h2 className="font-display text-3xl sm:text-4xl leading-tight" style={{ color: "#FBF6EE" }}>
                  {occasion.title}
                </h2>
                <p className="mt-3 font-body text-xs tracking-[0.18em] uppercase" style={{ color: "rgba(232,217,197,0.7)" }}>
                  {occasion.context}
                </p>
              </div>
            </Reveal>
            <div className="mt-12 grid md:grid-cols-2 gap-x-14 gap-y-14">
              {occasion.pieces.map((piece) => (
                <Reveal key={`${piece.publication}-${piece.date}`} className={piece.wide ? "md:col-span-2" : ""}>
                  <Piece piece={piece} />
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      ))}
    </>
  );
}
